import React, { useEffect, useState, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Table,
  Button,
  Card,
  Input,
  Space,
  Popconfirm,
  Spin,
  Select,
  Tooltip,
  message,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SearchOutlined,
  GoogleOutlined,
  BankOutlined,
  PhoneOutlined,
  MailOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../stores/root-store-context';
import { useNavigate } from 'react-router';
import { useGoogleLogin } from '@react-oauth/google';
import CrmUserStamp from '../../components/crm/CrmUserStamp';
import { formatGoogleError } from '../../helpers/googleErrorFormatter';
import { isValidPhoneNumber, normalizeDigits } from '../../helpers/crmDeduplication';
import { Contact } from '../../api/CRMApiClient';
import './crm.css';

const { Option } = Select;

const ContactManagement: React.FC = observer(() => {
  const { t } = useTranslation();
  const { applicationStore, crmStore } = useStore();
  const navigate = useNavigate();

  /* ───────────────────────────────────── filters ─────────────────────────────────── */

  const [search, setSearch] = useState<string>('');
  const [filterRole, setFilterRole] = useState<number | null>(null);
  const [filterCompanies, setFilterCompanies] = useState<number[]>([]);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterRole, filterCompanies]);

  const resetFilters = () => {
    setSearch('');
    setFilterRole(null);
    setFilterCompanies([]);
    setCurrentPage(1);
  };

  const filteredContacts = useMemo(() => {
    return crmStore.contacts
      .filter((c) => {
        const q = search.toLowerCase();
        const matchesSearch = q
          ? [c.name, c.surname, c.email, c.phoneNumber]
              .filter(Boolean)
              .some((v) => v!.toLowerCase().includes(q))
          : true;
        const matchesRole = filterRole ? c.roleId === filterRole : true;
        const matchesCompany = filterCompanies.length > 0 ? (c.externalCompanyId ? filterCompanies.includes(c.externalCompanyId) : false) : true;

        return matchesSearch && matchesRole && matchesCompany;
      })
      .sort((a, b) => b.id - a.id);
  }, [crmStore.contacts, search, filterRole, filterCompanies]);

  const rangeStart = filteredContacts.length > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(currentPage * pageSize, filteredContacts.length);

  /* ───────────────────────────────────── data loading ───────────────────────────── */

  useEffect(() => {
    Promise.all([
      crmStore.getContacts(),
      crmStore.getClientRoles(),
      crmStore.getExternalCompanies(),
    ]);
  }, [crmStore]);

  const [cachedGoogleToken, setCachedGoogleToken] = useState<string | null>(() => {
    return sessionStorage.getItem('google_contacts_access_token');
  });

  const { syncToast, setSyncToast } = crmStore;

  const [singleSyncingId, setSingleSyncingId] = useState<number | null>(null);
  const pendingSingleContactRef = useRef<number | null>(null);

  const isGoogleContactsEnabled =
    applicationStore.company?.EnableGoogleContactsSync ||
    applicationStore.company?.EnableGoogleSync ||
    true;

  const handleCopy = (text: string, e: React.MouseEvent, label: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    message.success(`${label}: ${t('crm.copied', 'Скопійовано в буфер!')}`);
  };

  /* ───────────────────────────────────── columns ─────────────────────────────────── */

  const columns: any[] = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: '4%',
      sorter: (a: any, b: any) => a.id - b.id,
      defaultSortOrder: 'descend',
    },
    {
      title: t('crm.contact', 'Контакт'),
      key: 'contact',
      width: '20%',
      sorter: (a: any, b: any) => `${a.name || ''} ${a.surname || ''}`.localeCompare(`${b.name || ''} ${b.surname || ''}`),
      render: (_: unknown, record: any) => {
        const fullName = `${record.name || ''} ${record.surname || ''}`.trim() || '—';
        const roleName = crmStore.clientRoles.find((r) => r.id === record.roleId)?.name;

        return (
          <div className="crm-contact-cell">
            <div className="crm-contact-info">
              <span className="crm-contact-name">{fullName}</span>
              {roleName && <span className="crm-contact-role-sub">{roleName}</span>}
            </div>
          </div>
        );
      },
    },
    {
      title: t('Phone'),
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: '15%',
      sorter: (a: any, b: any) => (a.phoneNumber || '').localeCompare(b.phoneNumber || ''),
      render: (phone: string) => {
        if (!phone) return <span style={{ color: '#cbd5e1' }}>—</span>;
        return (
          <div className="crm-interactive-cell" onClick={(e) => e.stopPropagation()}>
            <a
              href={`tel:${phone.replace(/\s+/g, '')}`}
              className="crm-interactive-link"
              title={phone}
            >
              <PhoneOutlined style={{ color: '#059669', fontSize: 13 }} />
              <span>{phone}</span>
            </a>
            <Tooltip title={t('crm.copyPhone', 'Скопіювати телефон')}>
              <button
                type="button"
                className="crm-copy-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCopy(phone, e, t('Phone'));
                }}
              >
                <CopyOutlined />
              </button>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: t('Email'),
      dataIndex: 'email',
      key: 'email',
      width: '17%',
      sorter: (a: any, b: any) => (a.email || '').localeCompare(b.email || ''),
      render: (email: string) => {
        if (!email) return <span style={{ color: '#cbd5e1' }}>—</span>;
        return (
          <div className="crm-interactive-cell" onClick={(e) => e.stopPropagation()}>
            <a
              href={`mailto:${email.trim()}`}
              className="crm-interactive-link"
              title={email}
            >
              <MailOutlined style={{ color: '#2563eb', fontSize: 13 }} />
              <span>{email}</span>
            </a>
            <Tooltip title={t('crm.copyEmail', 'Скопіювати email')}>
              <button
                type="button"
                className="crm-copy-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCopy(email, e, t('Email'));
                }}
              >
                <CopyOutlined />
              </button>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: t('Company'),
      dataIndex: 'externalCompanyId',
      key: 'externalCompanyId',
      width: '16%',
      sorter: (a: any, b: any) => {
        const cA = crmStore.externalCompanies.find((c) => c.id === a.externalCompanyId)?.name || '';
        const cB = crmStore.externalCompanies.find((c) => c.id === b.externalCompanyId)?.name || '';
        return cA.localeCompare(cB);
      },
      render: (companyId: number) => {
        const comp = crmStore.externalCompanies.find((c) => c.id === companyId);
        if (!comp) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
        return (
          <div
            className="crm-company-pill"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/${applicationStore.company?.Tenant}/crm/company/${companyId}`);
            }}
            title={comp.name}
          >
            <BankOutlined style={{ color: '#6366f1', fontSize: 13 }} />
            <span>{comp.name}</span>
          </div>
        );
      },
    },
    {
      title: t('CreatedBy'),
      key: 'createdBy',
      width: '12%',
      sorter: (a: any, b: any) => (a.createdAt || '').localeCompare(b.createdAt || ''),
      render: (_: unknown, r: any) => <CrmUserStamp name={r.createdByName} date={r.createdAt} />,
    },
    {
      title: t('UpdatedBy'),
      key: 'updatedBy',
      width: '12%',
      sorter: (a: any, b: any) => (a.updatedAt || '').localeCompare(b.updatedAt || ''),
      render: (_: unknown, r: any) => <CrmUserStamp name={r.updatedByName} date={r.updatedAt} />,
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: '12%',
      render: (_: unknown, record: any) => {
        const isPhoneValid = Boolean(record.phoneNumber) && isValidPhoneNumber(record.phoneNumber) && normalizeDigits(record.phoneNumber).length >= 9;
        return (
          <Space size="small" onClick={(e) => e.stopPropagation()}>
            {isGoogleContactsEnabled && (
              <Tooltip
                title={
                  isPhoneValid
                    ? t('SyncWithGoogle', 'Синхронізувати з Google')
                    : t('InvalidPhoneForGoogle', 'Для синхронізації потрібен коректний номер телефону')
                }
              >
                <span>
                  <Button
                    className="crm-action-btn crm-action-btn-google"
                    icon={<GoogleOutlined style={{ color: isPhoneValid ? '#4285F4' : '#cbd5e1' }} />}
                    disabled={!isPhoneValid || singleSyncingId === record.id || crmStore.isGoogleSyncing}
                    loading={singleSyncingId === record.id}
                    onClick={() => handleSyncSingleContact(record)}
                  />
                </span>
              </Tooltip>
            )}
            <Button
              className="crm-action-btn crm-action-btn-edit"
              icon={<EditOutlined />}
              onClick={() =>
                navigate(`/${applicationStore.company?.Tenant}/crm/contact/${record.id}?edit=1`)
              }
            >
              {t('Edit')}
            </Button>
            <Popconfirm
              title={t('DeleteContactConfirm')}
              onConfirm={() => crmStore.deleteContact(record.id)}
            >
              <Button
                className="crm-action-btn crm-action-btn-delete"
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const triggerGoogleOAuthSync = useGoogleLogin({
    flow: 'auth-code',
    scope: 'https://www.googleapis.com/auth/contacts https://www.googleapis.com/auth/userinfo.email',
    onSuccess: async (codeResponse) => {
      console.log('[GoogleContacts] Received OAuth auth code from Google:', codeResponse);
      try {
        const connRes = await crmStore.connectGoogleContacts(codeResponse.code);
        if (connRes && 'connected' in connRes && connRes.connected) {
          await applicationStore.updateCompany();
        }
      } catch (e) {
        console.warn('[GoogleContacts] Failed to persist Google connection:', e);
      }

      const singleId = pendingSingleContactRef.current;
      pendingSingleContactRef.current = null;

      if (singleId) {
        setSingleSyncingId(singleId);
        setSyncToast({
          visible: true,
          status: 'syncing',
          totalCount: 1,
        });
        try {
          const res = await crmStore.syncSingleContactToGoogle(singleId);
          if (res && res.resourceName && !res.error) {
            setSyncToast({
              visible: true,
              status: 'success',
              syncedCount: 1,
            });
          } else {
            console.error('[GoogleContacts] Single sync failed with response:', res);
            const errMsg = formatGoogleError(res?.error || 'Failed to sync contact', t);
            setSyncToast({
              visible: true,
              status: 'error',
              errorMessage: errMsg,
            });
          }
        } catch (err: any) {
          console.error('[GoogleContacts] Single sync exception:', err);
          const errMsg = formatGoogleError(err, t);
          setSyncToast({
            visible: true,
            status: 'error',
            errorMessage: errMsg,
          });
        } finally {
          setSingleSyncingId(null);
        }
        return;
      }

      setSyncToast({
        visible: true,
        status: 'syncing',
        totalCount: filteredContacts.length || crmStore.contacts.length,
      });
      try {
        const res = await crmStore.syncGoogleContacts();
        if (res && typeof res.syncedCount === 'number') {
          setSyncToast({
            visible: true,
            status: 'success',
            syncedCount: res.syncedCount,
          });
        } else {
          console.error('[GoogleContacts] Sync failed with response:', res);
          const errMsg = formatGoogleError(res?.error, t);
          setSyncToast({
            visible: true,
            status: 'error',
            errorMessage: errMsg,
          });
        }
      } catch (err: any) {
        console.error('[GoogleContacts] Sync exception:', err);
        const errMsg = formatGoogleError(err, t);
        setSyncToast({
          visible: true,
          status: 'error',
          errorMessage: errMsg,
        });
      }
    },
    onError: (error) => {
      console.error('[GoogleContacts] Google OAuth popup error:', error);
      pendingSingleContactRef.current = null;
      setSingleSyncingId(null);
      setSyncToast({
        visible: true,
        status: 'error',
        errorMessage: t('googleErrors.syncFailed', 'Failed to connect Google account.'),
      });
    },
  });

  const handleSyncSingleContact = async (contact: Contact) => {
    if (!contact.id || crmStore.isGoogleSyncing || singleSyncingId !== null) return;
    setSingleSyncingId(contact.id);
    setSyncToast({
      visible: true,
      status: 'syncing',
      totalCount: 1,
    });

    try {
      const res = await crmStore.syncSingleContactToGoogle(contact.id, cachedGoogleToken || undefined);
      if (res && res.resourceName && !res.error) {
        setSyncToast({
          visible: true,
          status: 'success',
          syncedCount: 1,
        });
        setSingleSyncingId(null);
        return;
      }

      console.warn('[GoogleContacts] Direct single sync failed:', res?.error);
      const errLower = (res?.error || '').toLowerCase();
      const isAuthExpired =
        errLower.includes('401') ||
        errLower.includes('unauthorized') ||
        errLower.includes('unauthenticated') ||
        errLower.includes('invalid authentication credentials') ||
        errLower.includes('invalid credential') ||
        errLower.includes('invalid_token') ||
        errLower.includes('invalid_grant') ||
        errLower.includes('expired') ||
        errLower.includes('err_google_unauthorized_client') ||
        errLower.includes('err_google_credentials_missing') ||
        errLower.includes('err_google_scope_insufficient');

      if (isAuthExpired) {
        sessionStorage.removeItem('google_contacts_access_token');
        setCachedGoogleToken(null);
        pendingSingleContactRef.current = contact.id;
        triggerGoogleOAuthSync();
        return;
      }

      const errMsg = formatGoogleError(res?.error, t);
      setSyncToast({
        visible: true,
        status: 'error',
        errorMessage: errMsg,
      });
    } catch (err: any) {
      console.error('[GoogleContacts] Single sync exception:', err);
      sessionStorage.removeItem('google_contacts_access_token');
      setCachedGoogleToken(null);
      const errMsg = formatGoogleError(err, t);
      setSyncToast({
        visible: true,
        status: 'error',
        errorMessage: errMsg,
      });
    } finally {
      setSingleSyncingId(null);
    }
  };

  const handleSyncGoogleContacts = async () => {
    if (crmStore.isGoogleSyncing) return;
    pendingSingleContactRef.current = null;
    setSyncToast({
      visible: true,
      status: 'syncing',
      totalCount: filteredContacts.length || crmStore.contacts.length,
    });

    try {
      const res = await crmStore.syncGoogleContacts(cachedGoogleToken || undefined);
      if (res && typeof res.syncedCount === 'number') {
        setSyncToast({
          visible: true,
          status: 'success',
          syncedCount: res.syncedCount,
        });
        return;
      }

      console.warn('[GoogleContacts] Direct batch sync failed:', res?.error);
      const errLower = (res?.error || '').toLowerCase();
      const isAuthExpired =
        errLower.includes('401') ||
        errLower.includes('unauthorized') ||
        errLower.includes('unauthenticated') ||
        errLower.includes('invalid authentication credentials') ||
        errLower.includes('invalid credential') ||
        errLower.includes('invalid_token') ||
        errLower.includes('invalid_grant') ||
        errLower.includes('expired') ||
        errLower.includes('err_google_unauthorized_client') ||
        errLower.includes('err_google_credentials_missing') ||
        errLower.includes('err_google_scope_insufficient');

      if (isAuthExpired) {
        sessionStorage.removeItem('google_contacts_access_token');
        setCachedGoogleToken(null);
        triggerGoogleOAuthSync();
        return;
      }

      // Show actual backend error in toast
      const errMsg = formatGoogleError(res?.error, t);
      setSyncToast({
        visible: true,
        status: 'error',
        errorMessage: errMsg,
      });
    } catch (err: any) {
      console.error('[GoogleContacts] Batch sync exception:', err);
      sessionStorage.removeItem('google_contacts_access_token');
      setCachedGoogleToken(null);
      const errMsg = formatGoogleError(err, t);
      setSyncToast({
        visible: true,
        status: 'error',
        errorMessage: errMsg,
      });
    }
  };

  /* ───────────────────────────────────── render ─────────────────────────────────── */

  return (
    <Spin spinning={crmStore.isLoading}>
      <Card bordered={false}>
        <div className="crm-filter-toolbar">
          <div className="crm-filter-inputs">
            <Input
              placeholder={t('Search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              style={{ width: 220 }}
            />

            <Select
              placeholder={t('Role')}
              allowClear
              style={{ width: 180 }}
              value={filterRole ?? undefined}
              onChange={(val) => setFilterRole(val ?? null)}
            >
              {crmStore.clientRoles.map((role) => (
                <Option key={role.id} value={role.id}>
                  {role.name}
                </Option>
              ))}
            </Select>

            <Select
              mode="multiple"
              maxTagCount="responsive"
              placeholder={t('Company')}
              allowClear
              style={{ minWidth: 220, maxWidth: 320 }}
              value={filterCompanies}
              onChange={(val) => setFilterCompanies(val || [])}
            >
              {crmStore.externalCompanies.map((company) => (
                <Option key={company.id} value={company.id}>
                  {company.name}
                </Option>
              ))}
            </Select>

            <Button
              className="crm-reset-btn"
              icon={<ReloadOutlined />}
              onClick={resetFilters}
              disabled={!search && !filterRole && filterCompanies.length === 0}
            >
              {t('Reset')}
            </Button>
          </div>

          <div className="crm-filter-actions">
            <div className="crm-gmail-counter">
              <span>
                {rangeStart}–{rangeEnd} {t('crm.of', 'із')} {filteredContacts.length}
              </span>
              {filteredContacts.length !== crmStore.contacts.length && (
                <span className="crm-gmail-counter-secondary">
                  ({t('crm.total', 'всього')} {crmStore.contacts.length})
                </span>
              )}
            </div>

            {isGoogleContactsEnabled && (
              <Button
                className="crm-google-sync-btn"
                icon={<GoogleOutlined style={{ color: '#4285F4' }} />}
                loading={crmStore.isGoogleSyncing}
                disabled={crmStore.isGoogleSyncing}
                onClick={handleSyncGoogleContacts}
                title={t('SyncAllWithGoogleTooltip', 'Sync all contacts with Google Contacts')}
              >
                {t('SyncWithGoogle', 'Sync with Google')}
              </Button>
            )}
            <Button
              type="primary"
              className="crm-primary-btn"
              icon={<PlusOutlined />}
              onClick={() =>
                navigate(`/${applicationStore.company?.Tenant}/crm/contact/new`)
              }
            >
              {t('AddContact')}
            </Button>
          </div>
        </div>

        <Table
          className="crm-table"
          dataSource={filteredContacts}
          columns={columns}
          rowKey="id"
          rowClassName={() => 'crm-row-clickable'}
          onRow={(row: any) => ({ onClick: () => navigate(`/${applicationStore.company?.Tenant}/crm/contact/${row.id}`) })}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
            onChange: (page, size) => {
              setCurrentPage(page);
              if (size && size !== pageSize) {
                setPageSize(size);
              }
            },
          }}
        />
      </Card>
    </Spin>
  );
});

export default ContactManagement;
